type Constructor<T = HTMLElement> = new (...args: any[]) => T;

/** Custom elements carry this lifecycle callback at runtime; HTMLElement's own type doesn't declare it. */
interface HasConnectedCallback {
  connectedCallback?(...args: any[]): void;
}

const warned = new Set<string>();

/**
 * Warn about a deprecated API, once per page per name. Shared by every
 * deprecation path (element tags via DeprecatedEl below, store/method aliases)
 * so a page gets one warning per surface, not one per call.
 */
export function warnDeprecated(name: string, message: string): void {
  if (warned.has(name)) return;
  warned.add(name);
  console.warn(message);
}

/**
 * Wrap a custom element class in a deprecated alias constructor that warns once
 * per page when the deprecated tag is first connected.
 */
export function DeprecatedEl<TBase extends Constructor<HTMLElement & HasConnectedCallback>>(
  Base: TBase,
  deprecatedTag: string,
  replacementTag: string,
  warning?: string,
): TBase {
  return class DeprecatedElement extends Base {
    connectedCallback(...args: any[]): void {
      warnDeprecated(deprecatedTag, warning ?? `<${deprecatedTag}> is deprecated — use <${replacementTag}> instead.`);
      super.connectedCallback?.(...args);
    }
  } as TBase;
}
